import { IAgreement } from 'models/agreements'
import { SwitchButton, SwitchButtonState } from 'components/SwitchButton/SwitchButton'
import { base64ToByteArray, handleTextInputNumberChange, hexToBase64, isMobileDevice, toHexString } from 'helpers'
import { canSignAgreement, getSigningErrorMessage, GENERIC_SIGNING_ERROR } from 'helpers/signer-helper'
import React, { useState } from 'react'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'
import AgreementsService from 'services/agreements.service'
import { InitContextProps, useAuthContext } from 'services/context/authentication.context'
import * as webEid from '@web-eid/web-eid-library/web-eid';
import { POLL_MAX_RETRY_COUNT_ON_FAILED_REQUEST } from 'services/authentication.service'

interface AgreementSigningModalProps {
  agreement: IAgreement | null;
  handleModalHide: () => void;
  onSubmitSuccess: () => void;
}

export default function AgreementSigningModal(props: AgreementSigningModalProps) {

  const isMobile = isMobileDevice();

  const { state }: InitContextProps = useAuthContext();
  const { customerId, customer, user } = state;

  const [saving, setSaving]: [boolean, any] = useState(false);
  const [savingError, setSavingError]: [boolean, any] = useState(false);
  const [errorMessage, setErrorMessage]: [string | null, any] = useState(null);
  const [method, setMethod] = useState<string>('smart-id');
  const [activeSwitch, setActiveSwitch] = useState<SwitchButtonState>(SwitchButtonState.LEFT);
  const [personalCode, setPersonalCode] = useState<string>((user && user.personalIdentificationCode) || '');
  const [phoneNumber, setPhoneNumber] = useState<string>((customer && customer.phone) || '');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [intervalId, setIntervalId] = useState<number | undefined>();
  const [retryCount, setRetryCount] = useState(0);

  const confirmSubmit = () => {
    if (props.agreement && customerId) {
      // ID-card signing takes the code from the certificate, so it can only be checked once signing has started.
      // Smart-ID and Mobile-ID take a typed code, which lets someone enter a person who is not a required signer.
      if (method !== 'id-card' && !canSignAgreement(props.agreement, personalCode)) {
        setSavingError(true);
        setErrorMessage('Sisestatud isik ei ole selle lepingu allkirjastaja');
        return;
      }

      setSavingError(false);
      setErrorMessage(null);

      if (method === 'smart-id') {
        beginSmartIdSigning();
      } else if (method === 'id-card') {
        beginIdCardSigning();
      } else {
        beginMobileIdSigning();
      }
    }
  }
  const beginSmartIdSigning = () => {
    if (!customerId || !props.agreement || !personalCode) {
      return;
    }
    setSaving(true);
    setRetryCount(0);
    AgreementsService.initSmartIdSigning(customerId, props.agreement.id, personalCode, state.customer!.contactId).then(res => {
      setVerificationCode(res);
      const interval: any = setInterval(() => {
        if (!props.agreement) {
          return clearInterval(interval);
        }
        AgreementsService.pollSmartIdSigning(customerId, props.agreement.id, personalCode, res, state.customer!.contactId).then(pollResult => {
          if (pollResult.result === 'COMPLETED') {
            clearInterval(interval);
            setSaving(false);
            setVerificationCode('');
            props.onSubmitSuccess();
          } else if (pollResult.result === 'FAILED') {
            clearInterval(interval);
            setSaving(false);
            setSavingError(true);
            setErrorMessage(GENERIC_SIGNING_ERROR)
          }
        }).catch(e => {
          if (retryCount === POLL_MAX_RETRY_COUNT_ON_FAILED_REQUEST) {
            clearInterval(interval);
            setSaving(false);
            setSavingError(true);
            setErrorMessage(GENERIC_SIGNING_ERROR)
          } else {
            setRetryCount(retryCount + 1);
          }

        })
      }, 1000);
      setIntervalId(interval);
    }).catch(e => {
      setSaving(false);
      setSavingError(true);
      setErrorMessage(getSigningErrorMessage(e))
    })
  }

  const beginIdCardSigning = () => {
    if (!customerId) {
      return;
    }
    webEid.status()
      .then(() => {
        webEid.getSigningCertificate({ lang: 'et' })
          .then(({ certificate }) => {
            if (!props.agreement) {
              return null;
            }
            setSaving(true);
            AgreementsService.initIdCardSigning(customerId, props.agreement.id, toHexString(base64ToByteArray(certificate)), state.customer!.contactId).then((initResult) => {
              webEid.sign(certificate, hexToBase64(initResult.hex), 'SHA-256', {lang: 'et'}).then(
                ({ signature }) => {
                  if (!props.agreement) {
                    return null;
                  }
                  AgreementsService.signIdCardAgreement(customerId, props.agreement.id, toHexString(base64ToByteArray(signature)), state.customer!.contactId)
                  .then(() => {
                    setSaving(false);
                    props.onSubmitSuccess();
                  }).catch((e) => {
                    setSaving(false);
                    setSavingError(true);
                    setErrorMessage(getSigningErrorMessage(e));
                  });
                }
              );
            }).catch((e) => {
            setSaving(false);
            setSavingError(true);
            setErrorMessage(getSigningErrorMessage(e));
          });
          }).catch((e) => {
            setSaving(false);
            setSavingError(true);
            console.error(e)
            setErrorMessage(GENERIC_SIGNING_ERROR);
          });
      }).catch((e) => {
        setSaving(false);
        setSavingError(true);
        console.error(e)
        setErrorMessage(GENERIC_SIGNING_ERROR);
      });
  }

  const beginMobileIdSigning = () => {
    if (!customerId || !props.agreement || !phoneNumber || !personalCode) {
      return;
    }
    setSaving(true);
    setRetryCount(0);
    AgreementsService.initMobileIdSigning(customerId, props.agreement.id, personalCode, phoneNumber, state.customer!.contactId).then(res => {
      setVerificationCode(res);
      const interval: any = setInterval(() => {
        if (!customerId || !props.agreement) {
          return clearInterval(interval);
        }
        AgreementsService.pollMobileIdSigning(customerId, props.agreement.id, personalCode, res, state.customer!.contactId).then(pollResult => {
          if (pollResult.result === 'COMPLETED') {
            clearInterval(interval);
            setSaving(false);
            setVerificationCode('');
            props.onSubmitSuccess();
          } else if (pollResult.result === 'FAILED') {
            clearInterval(interval);
            setSaving(false);
            setSavingError(true);
            setErrorMessage(GENERIC_SIGNING_ERROR)
          }
        }).catch(() => {
          if (retryCount === POLL_MAX_RETRY_COUNT_ON_FAILED_REQUEST) {
            clearInterval(interval);
            setSaving(false);
            setSavingError(true);
            setErrorMessage(GENERIC_SIGNING_ERROR)
          } else {
            setRetryCount(retryCount + 1);
          }
        })
      }, 1000);
      setIntervalId(interval);
    }).catch((e) => {
      setSaving(false);
      setSavingError(true);
      setErrorMessage(getSigningErrorMessage(e));
    })

  }

  const onMethodChange = (active: SwitchButtonState) => {
    let methodState = '';
    if (active === SwitchButtonState.LEFT) {
      methodState = 'smart-id';
    } else if (active === SwitchButtonState.MIDDLE) {
      methodState = 'id-card';
    } else {
      methodState = 'mobile-id';
    }
    setActiveSwitch(active);
    setMethod(methodState);
  }

  const onCancel = () => {
    if (intervalId) {
      clearInterval(intervalId);
    }
    props.handleModalHide();
  }

  // Signing is only allowed as the logged in user, so the code is prefilled and locked when it is known.
  const isPersonalCodeLocked = !!(user && user.personalIdentificationCode);

  const personalCodeInput = (
    <input
      type="text"
      className="login__smart-id-input mb-1 mw-phone-input"
      placeholder='Isikukood'
      value={personalCode}
      onChange={(e) => setPersonalCode(handleTextInputNumberChange(personalCode, e.target.value))}
      disabled={saving || isPersonalCodeLocked}
    />
  );

  const getForm = () => {
    if (method === 'smart-id') {
      return (
        <div className="d-flex flex-column align-items-center login__container">
          {personalCodeInput}
      </div>
      );
    } else if (method === 'id-card') {
      return (
        <div>Sisesta ID-kaart lugejasse ja vajuta nupule "Allkirjasta"</div>
      );
    } else {
      return (
        <div className="d-flex flex-column align-items-center login__container">
          {personalCodeInput}
          <div className="d-flex flex-row justify-content-center">
            <input
              type="text"
              className="mobile-id-input__prefix mr-1"
              placeholder='+372'
              disabled
            />
            <input
              type="tel"
              className="mobile-id-input__main"
              placeholder='Telefoninumber'
              disabled={saving}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(handleTextInputNumberChange(phoneNumber, e.target.value))}
            />
          </div>
        </div>
      );
    }
  }  

  return (
      <Modal
        show={true}
        size={"lg"}
        backdrop={'static'}
        onHide={props.handleModalHide}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Lepingu allkirjastamine
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form noValidate onSubmit={(e: any) => {
            e.preventDefault();
            confirmSubmit();
          }}
            id={"card-form"}
          >
            <div className="mt-4 row">
              <SwitchButton
                className={"default-switch mx-auto "}
                onActiveChange={onMethodChange}
                labelLeft={"Smart-ID"}
                labelMiddle={"ID-kaart"}
                labelRight={"Mobiil-ID"}
                enableMiddleButton={!isMobile}
                active={activeSwitch}
              />
            </div>
            <div className="mt-5 row">
              <div className="col-12 col-lg-8 offset-lg-2 text-center">
              {
                saving && verificationCode.length > 0 &&
                <>
                  <p>Teie kontrollkood on</p>
                  <strong className="h1">{verificationCode}</strong>
                </>
              }
              {
                getForm()
              }
              </div>
            </div>

          </Form>
        </Modal.Body>
        <Modal.Footer className="d-flex flex-row align-items-end justify-content-around flex-wrap mb-1">
          <Button variant="outline-dark"
            onClick={onCancel}
            type="button"
            className={"d-flex align-items-center"}>
            <span className='px-2'>Loobu</span>
          </Button>
          <Button variant="primary" onClick={confirmSubmit} type="submit" disabled={saving} form="card-form"
            className="d-flex align-items-center">
            {
              !saving &&
              <span className='px-2'>Allkirjasta</span>
            }
            {
              saving &&
              <>
                <span className='px-2'>Allkirjastamine…</span>
                <Spinner size={"sm"} animation={"border"} />
              </>
            }
          </Button>
        </Modal.Footer>
        {
          !saving && savingError &&
          <span className="d-flex justify-content-end text-danger px-5 pb-2">
            {errorMessage || 'Viga allkirjastamisel, proovi mõne hetke pärast uuesti.'}
          </span>
        }
      </Modal>
  )

}